import type { ThemeAppearance } from '@atlaskit/lozenge';

import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';

import { DEFAULT_PLUGIN_LOZENGE_APPEARANCE, PLUGIN_LOZENGE_APPEARANCE } from './constants';
import {
	type AnswerGenerationModel,
	type CacheInfo,
	type FunctionResultType,
	MinionName,
	type MinionOutput,
	type MinionOutputItem,
	type MinionOverviewInfo,
	type MinionPluginInvocationInfo,
	type NormalizedDebuggerData,
	type OrchestrationRound,
	type PageSearchOutput,
	type PageSearchPage,
	type ParsedMinionData,
	type ParsedPluginOutput,
	type PeopleOutput,
	type PeopleProfile,
	type PluginExtraInfo,
	type PluginInvocation,
	PluginName,
	type RankedDocumentWithChunks,
	type RawDebuggerData,
	type TeamworkDocument,
	type TeamworkOutput,
	type TeamworkTeam,
} from './types';

/**
 * Extracts search results from plugin invocations.
 * Finds the search plugin and parses its plugin_extra_info to get searchResults.
 */
export const getSearchResultsFromPlugins = (
	pluginInvocations: PluginInvocation[] | undefined,
): PluginExtraInfo | null => {
	if (!Array.isArray(pluginInvocations)) {
		return null;
	}

	const searchPlugin = pluginInvocations.find((plugin) => plugin.plugin_name === PluginName.SEARCH);

	if (!searchPlugin?.plugin_extra_info) {
		return null;
	}

	try {
		return JSON.parse(searchPlugin.plugin_extra_info) as PluginExtraInfo;
	} catch {
		return null;
	}
};

/**
 * Extracts search results from minion outputs.
 * Finds the search minion and parses its minion_extra_info to get searchResults.
 *
 * The search minion's data structure is:
 * minion_extra_info → JSON → functionMessage.pluginInvocationInfo.extraInfo → JSON → PluginExtraInfo
 */
export const getSearchResultsFromMinions = (
	minionOutputs: MinionOutput[] | undefined,
): PluginExtraInfo | null => {
	if (!Array.isArray(minionOutputs)) {
		return null;
	}

	// Find search minion - check if minion_id contains 'search' (case insensitive)
	const searchMinion = minionOutputs.find((minion) => {
		const minionInfo = Array.isArray(minion) ? minion[0] : minion;
		return minionInfo.minion_id?.toLowerCase().includes(MinionName.SEARCH);
	});

	if (!searchMinion) {
		return null;
	}

	const minionInfo = Array.isArray(searchMinion) ? searchMinion[0] : searchMinion;

	if (!minionInfo.minion_extra_info) {
		return null;
	}

	try {
		const parsedExtraInfo = JSON.parse(minionInfo.minion_extra_info);

		// Extract from functionMessage.pluginInvocationInfo.extraInfo
		if (parsedExtraInfo.functionMessage?.pluginInvocationInfo?.extraInfo) {
			try {
				return JSON.parse(
					parsedExtraInfo.functionMessage.pluginInvocationInfo.extraInfo,
				) as PluginExtraInfo;
			} catch {
				return null;
			}
		}

		return null;
	} catch {
		return null;
	}
};

/**
 * Merges search extra info from multiple sources (plugins and minions).
 * Plugin data takes precedence, minion data is used as fallback or to supplement.
 */
export const mergeSearchExtraInfo = (
	pluginSearchInfo: PluginExtraInfo | null,
	minionSearchInfo: PluginExtraInfo | null,
): PluginExtraInfo | null => {
	// If we have plugin search info, use it (it has more complete data like rawRankedDocumentsInfoMap)
	if (pluginSearchInfo) {
		return pluginSearchInfo;
	}

	// Otherwise, use minion search info
	return minionSearchInfo;
};

/**
 * Matches ranked documents with their chunks from rawRankedDocumentsInfoMap.
 * The rawRankedDocumentsInfoMap object keys are in the same order as the rankedDocuments array,
 * so we can match them by index position.
 * Note: In modern JavaScript (ES2015+), object property order is preserved for string keys.
 */
export const getRankedDocumentsWithChunks = (
	searchExtraInfo: PluginExtraInfo | null,
): RankedDocumentWithChunks[] => {
	if (!searchExtraInfo?.rankedDocuments || !searchExtraInfo?.rawRankedDocumentsInfoMap) {
		return [];
	}

	const { rankedDocuments, rawRankedDocumentsInfoMap } = searchExtraInfo;
	const chunkArrays = Object.values(rawRankedDocumentsInfoMap);

	return rankedDocuments.map((url, index) => ({
		url,
		chunks: chunkArrays[index]?.sort((a, b) => a.chunk_id - b.chunk_id) ?? [],
	}));
};

type ParsedMarkdownLink = {
	name: string;
	url: string;
	accountId: string | null;
};

/**
 * Parses a markdown-style link and extracts the display name and accountId.
 *
 * Handles links in format: [Name](/people/accountId)
 * Returns the name with accountId appended in parentheses if found.
 *
 * @param text - The text that may contain a markdown link
 * @returns Object with name, url, accountId, or null if not a valid link
 */
export const parseMarkdownLink = (text: string): ParsedMarkdownLink | null => {
	const match = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
	if (!match) {
		return null;
	}

	const [, name, url] = match;
	const accountIdMatch = url.match(/\/people\/([^/]+)/);
	const accountId = accountIdMatch ? accountIdMatch[1] : null;

	return { name, url, accountId };
};

/**
 * Formats a markdown link as display text with accountId.
 *
 * @param text - The text that may contain a markdown link
 * @returns Formatted string like "Name (accountId)" or original text if not a link
 */
export const formatMarkdownLinkAsText = (text: string): string => {
	const parsed = parseMarkdownLink(text);
	if (!parsed) {
		return text;
	}
	return parsed.accountId ? `${parsed.name} (${parsed.accountId})` : parsed.name;
};

export const parseTeamworkOutput = (output: string): TeamworkOutput | null => {
	const lines = output.split('\n');
	const result: TeamworkOutput = { sections: [] };
	let currentTeam: Partial<TeamworkTeam> | null = null;
	let currentDocument: Partial<TeamworkDocument> | null = null;
	let inMembersSection = false;
	let headerLines: string[] = [];
	let currentSection: {
		sectionHeader?: string;
		teams?: TeamworkTeam[];
		documents?: TeamworkDocument[];
	} = {};

	lines.forEach((line) => {
		const trimmedLine = line.trim();

		// Capture main header
		if (trimmedLine.startsWith('Here are') && !result.header && !currentTeam && !currentDocument) {
			headerLines.push(trimmedLine);
			return;
		}

		// Section headers (like "Here are the Confluence documents:")
		if (
			trimmedLine.startsWith('Here are the') &&
			trimmedLine.endsWith(':') &&
			!currentTeam &&
			!currentDocument
		) {
			// Save previous section if exists
			if (currentSection.teams || currentSection.documents) {
				result.sections.push(currentSection);
			}
			currentSection = { sectionHeader: trimmedLine.slice(0, -1) }; // Remove trailing colon
			return;
		}

		// Start of a new team
		if (trimmedLine.startsWith('- type: Team')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentTeam = { type: 'Team', members: [] };
			currentDocument = null;
			inMembersSection = false;
			return;
		}

		// Start of a new document (Confluence Page, Confluence Blog Post, etc.)
		if (trimmedLine.startsWith('- type: Confluence') || trimmedLine.startsWith('- type: Jira')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentDocument = { type: trimmedLine.substring(8).trim() };
			currentTeam = null;
			inMembersSection = false;
			return;
		}

		// Parse fields
		if (currentTeam) {
			// Team parsing
			if (inMembersSection && trimmedLine.startsWith('- [')) {
				currentTeam.members!.push(trimmedLine.substring(2).trim());
			} else if (trimmedLine.startsWith('- ')) {
				const colonIndex = trimmedLine.indexOf(':');
				if (colonIndex !== -1) {
					const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
					const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

					switch (fieldName) {
						case 'ari':
							currentTeam.ari = fieldValue;
							break;
						case 'name':
							currentTeam.name = fieldValue;
							break;
						case 'description':
							currentTeam.description = fieldValue;
							break;
						case 'creator':
							currentTeam.creator = fieldValue;
							break;
						case 'member count':
							currentTeam.memberCount = parseInt(fieldValue, 10);
							break;
						case 'teamstate':
							currentTeam.teamState = fieldValue;
							inMembersSection = false;
							break;
						case 'members':
							inMembersSection = true;
							currentTeam.members = [];
							break;
					}
				}
			}
		} else if (currentDocument && trimmedLine.startsWith('- ')) {
			// Document parsing
			const colonIndex = trimmedLine.indexOf(':');
			if (colonIndex !== -1) {
				const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
				const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

				switch (fieldName) {
					case 'ari':
						currentDocument.ari = fieldValue;
						break;
					case 'title':
						currentDocument.title = fieldValue;
						break;
					case 'url':
						currentDocument.url = fieldValue;
						break;
					case 'author':
						currentDocument.author = fieldValue;
						break;
					case 'updated':
						currentDocument.updated = fieldValue;
						break;
				}
			}
		}
	});

	// Save last item
	if (currentTeam) {
		if (!currentSection.teams) {
			currentSection.teams = [];
		}
		currentSection.teams.push(currentTeam as TeamworkTeam);
	}
	if (currentDocument) {
		if (!currentSection.documents) {
			currentSection.documents = [];
		}
		currentSection.documents.push(currentDocument as TeamworkDocument);
	}

	// Save last section
	if (currentSection.teams || currentSection.documents) {
		result.sections.push(currentSection);
	}

	if (headerLines.length > 0) {
		result.header = headerLines.join(' ');
	}

	return result.sections.length > 0 ? result : null;
};

export const parsePeopleOutput = (output: string): PeopleOutput | null => {
	const lines = output.split('\n');
	const result: PeopleOutput = { people: [] };
	let currentPerson: { header?: string; accountId?: string; profile: PeopleProfile } | null = null;
	let inProfileSection = false;

	const savePerson = () => {
		if (currentPerson && currentPerson.profile.name) {
			result.people.push(currentPerson);
		}
	};

	for (const line of lines) {
		const trimmedLine = line.trim();

		// Parse header line to extract account ID - this starts a new person
		if (trimmedLine.startsWith('Retrieved the following information for user')) {
			// Save previous person if exists
			savePerson();

			// Start new person
			currentPerson = { profile: {} };
			currentPerson.header = trimmedLine;
			const accountIdMatch = trimmedLine.match(/account\/user id \(([^)]+)\)/);
			if (accountIdMatch) {
				currentPerson.accountId = accountIdMatch[1];
			}
			inProfileSection = false;
			continue;
		}

		// Check for profile section start
		if (trimmedLine === '# Profile:') {
			inProfileSection = true;
			continue;
		}

		// Parse profile fields (format: - **Key**: Value)
		if (inProfileSection && currentPerson && trimmedLine.startsWith('- **')) {
			const fieldMatch = trimmedLine.match(/^- \*\*([^*]+)\*\*:\s*(.*)$/);
			if (fieldMatch) {
				const [, fieldName, fieldValue] = fieldMatch;
				const profile = currentPerson.profile;

				switch (fieldName.toLowerCase()) {
					case 'name':
						profile.name = fieldValue;
						break;
					case 'picture':
						profile.picture = fieldValue;
						break;
					case 'email':
						profile.email = fieldValue;
						break;
					case 'timezone':
						profile.timezone = fieldValue;
						break;
					case 'locale':
						profile.locale = fieldValue;
						break;
					case 'nickname':
						profile.nickname = fieldValue;
						break;
					case 'job title':
						profile.jobTitle = fieldValue;
						break;
					case 'location':
						profile.location = fieldValue;
						break;
					case 'department':
						profile.department = fieldValue;
						break;
					case 'organization':
						profile.organization = fieldValue;
						break;
				}
			}
		}
	}

	// Save the last person
	savePerson();

	return result.people.length > 0 ? result : null;
};

export const parsePageSearchOutput = (output: string): PageSearchOutput | null => {
	const lines = output.split('\n');
	const result: PageSearchOutput = { pages: [] };
	let currentPage: Partial<PageSearchPage> | null = null;
	let headerCaptured = false;

	const savePage = () => {
		if (currentPage && currentPage.title) {
			result.pages.push(currentPage as PageSearchPage);
		}
	};

	for (const line of lines) {
		const trimmedLine = line.trim();

		// Capture header
		if (!headerCaptured && trimmedLine.startsWith('Here are')) {
			result.header = trimmedLine;
			headerCaptured = true;
			continue;
		}

		// Start of a new page entry (title line)
		if (trimmedLine.startsWith('- title:')) {
			// Save previous page if exists
			savePage();
			currentPage = { title: trimmedLine.substring(9).trim() };
			continue;
		}

		// Parse page fields
		if (currentPage) {
			if (trimmedLine.startsWith('url:')) {
				currentPage.url = trimmedLine.substring(4).trim();
			} else if (trimmedLine.startsWith('space:')) {
				currentPage.space = trimmedLine.substring(6).trim();
			} else if (trimmedLine.startsWith('date:')) {
				currentPage.date = trimmedLine.substring(5).trim();
			} else if (trimmedLine.startsWith('author:')) {
				currentPage.author = trimmedLine.substring(7).trim();
			} else if (trimmedLine.startsWith('snippet:')) {
				currentPage.snippet = trimmedLine.substring(8).trim();
			}
		}
	}

	// Save the last page
	savePage();

	return result.pages.length > 0 ? result : null;
};

export const parsePluginOutput = (
	pluginName: string | undefined,
	output: string,
): ParsedPluginOutput | null => {
	switch (pluginName) {
		case PluginName.TEAMWORK: {
			const teamworkData = parseTeamworkOutput(output);
			if (teamworkData) {
				return { type: 'teamwork', data: teamworkData };
			}
			break;
		}
		case PluginName.PAGE_SEARCH_PLUGIN: {
			const pageSearchData = parsePageSearchOutput(output);
			if (pageSearchData) {
				return { type: 'pageSearch', data: pageSearchData };
			}
			break;
		}
	}
	return null;
};

/**
 * Parses minion_extra_info to extract displayable data.
 *
 * The minion_extra_info can contain different structures depending on the minion type:
 *
 * 1. Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.extraInfo containing searchResults/rankedDocuments
 *    - Stores in searchExtraInfo for specialized rendering
 *
 * 2. Non-Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.output or functionMessage.output
 *    - Attempts to parse it using parsePluginOutput for structured display
 *
 * 3. Teamwork Agent with results array:
 *    - Extracts the results array containing function invocation results
 *    - Each result has pluginInvocationInfo with name, status, and output
 *
 * 4. Content fallback:
 *    - If none of the above, extracts the content field as plain text
 *
 * 5. Parse failure:
 *    - If JSON parsing fails, displays the raw minion_extra_info string
 */
export const parseMinionExtraInfo = (
	minionExtraInfo: string | undefined,
	minionId: string | undefined,
	isSearchMinion: boolean,
): ParsedMinionData => {
	let extraInfo: MinionPluginInvocationInfo | string | null = null;
	let parsedOutput: ParsedPluginOutput | null = null;
	let functionResults: FunctionResultType[] | null = null;
	let searchExtraInfo: PluginExtraInfo | null = null;

	if (!minionExtraInfo) {
		return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
	}

	try {
		const parsedExtraInfo = JSON.parse(minionExtraInfo);

		// Case 1 & 2: Has functionMessage (single function call result)
		if (parsedExtraInfo.functionMessage) {
			const { pluginInvocationInfo, output } = parsedExtraInfo.functionMessage;

			if (isSearchMinion && pluginInvocationInfo?.extraInfo) {
				// Search minion: extract nested search results from extraInfo
				try {
					searchExtraInfo = JSON.parse(pluginInvocationInfo.extraInfo);
				} catch {
					extraInfo = pluginInvocationInfo.extraInfo;
				}
			} else {
				// Non-search minion: parse the output for structured display
				const rawOutput = pluginInvocationInfo?.output || output;
				if (typeof rawOutput === 'string') {
					parsedOutput = parsePluginOutput(minionId, rawOutput);
					if (!parsedOutput) {
						extraInfo = rawOutput;
					}
				}
			}
		}
		// Case 3: Has results array (multiple function calls, e.g., Teamwork Agent)
		else if (Array.isArray(parsedExtraInfo.results) && parsedExtraInfo.results.length > 0) {
			functionResults = parsedExtraInfo.results as FunctionResultType[];
		}
		// Case 4: Has content field (plain text fallback)
		else if (parsedExtraInfo.content) {
			extraInfo = parsedExtraInfo.content;
		}
	} catch {
		// Case 5: JSON parsing failed, use raw string
		extraInfo = minionExtraInfo;
	}

	return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
};

/**
 * Gets the lozenge appearance for a plugin name.
 * Returns the mapped appearance if known, otherwise returns the default.
 */
export const getPluginLozengeAppearance = (pluginName: string | undefined): ThemeAppearance => {
	if (!pluginName) {
		return DEFAULT_PLUGIN_LOZENGE_APPEARANCE;
	}
	if (pluginName in PLUGIN_LOZENGE_APPEARANCE) {
		return PLUGIN_LOZENGE_APPEARANCE[pluginName as PluginName];
	}
	return DEFAULT_PLUGIN_LOZENGE_APPEARANCE;
};

// --- Helper Functions ---

/**
 * Extracts the model name from the answer generation model object.
 * Returns the first key from model_usage or 'null' if not available.
 */
export const extractModelName = (
	answerGenerationModel: AnswerGenerationModel | undefined,
): string => {
	if (answerGenerationModel?.model_usage && typeof answerGenerationModel.model_usage === 'object') {
		const keys = Object.keys(answerGenerationModel.model_usage);
		return keys[0] ?? 'null';
	}
	return 'null';
};

/**
 * Extracts unique plugin names from plugin invocations.
 * Filters out undefined/null plugin names.
 */
export const extractUniquePluginNames = (
	pluginInvocations: PluginInvocation[] | undefined,
): string[] => {
	if (!Array.isArray(pluginInvocations)) {
		return [];
	}
	return [
		...new Set(
			pluginInvocations.map((p) => p.plugin_name).filter((name): name is string => Boolean(name)),
		),
	];
};

/**
 * Filters plugin invocations to exclude the search plugin.
 * The search plugin data is displayed separately in the search results panels.
 */
export const filterNonSearchPlugins = (
	pluginInvocations: PluginInvocation[] | undefined,
): PluginInvocation[] => {
	if (!Array.isArray(pluginInvocations)) {
		return [];
	}
	return pluginInvocations.filter((plugin) => plugin.plugin_name !== PluginName.SEARCH);
};

/**
 * Filters minion outputs to exclude the search minion.
 * The search minion data is displayed separately in the search results panels.
 */
export const filterNonSearchMinions = (
	minionOutputs: MinionOutput[] | undefined,
): MinionOutput[] => {
	if (!Array.isArray(minionOutputs)) {
		return [];
	}
	return minionOutputs.filter((minion) => {
		const minionInfo = Array.isArray(minion) ? minion[0] : minion;
		return !minionInfo.minion_id?.toLowerCase().includes(MinionName.SEARCH);
	});
};

export const normalizeCacheHit = (cacheInfo: CacheInfo | undefined): boolean | null => {
	if (!cacheInfo) {
		return null;
	}
	return cacheInfo.cache_hit ?? null;
};

/**
 * Extracts the MinionOutputItem from a MinionOutput which can be
 * either a single item or an array of items.
 */
export const getMinionOutputItem = (minion: MinionOutput): MinionOutputItem => {
	return Array.isArray(minion) ? minion[0] : minion;
};

/**
 * Determines if the minion_outputs structure represents orchestration rounds.
 * Returns true if it's an array of arrays (rounds containing minions).
 */
export const isOrchestrationStructure = (minionOutputs: MinionOutput[] | undefined): boolean => {
	if (!Array.isArray(minionOutputs) || minionOutputs.length === 0) {
		return false;
	}
	// Check if first element is an array (indicating rounds structure)
	return Array.isArray(minionOutputs[0]);
};

/**
 * Organizes minion outputs into orchestration rounds.
 * If the structure is already rounds (array of arrays), returns as-is.
 * If it's a flat array, wraps it in a single round.
 */
export const getOrchestrationRounds = (
	minionOutputs: MinionOutput[] | undefined,
): OrchestrationRound[] => {
	if (!Array.isArray(minionOutputs) || minionOutputs.length === 0) {
		return [];
	}

	// Check if it's already a rounds structure (array of arrays)
	if (isOrchestrationStructure(minionOutputs)) {
		return minionOutputs as OrchestrationRound[];
	}

	// Wrap flat array in a single round
	return [minionOutputs];
};

/**
 * Creates a high-level overview of a minion, excluding detailed output.
 * Used for the Orchestration Overview tab.
 */
export const getMinionOverviewInfo = (minion: MinionOutput): MinionOverviewInfo => {
	const minionInfo = getMinionOutputItem(minion);
	return {
		minion_id: minionInfo.minion_id,
		minion_input: minionInfo.minion_input,
		minion_extra_info: minionInfo.minion_extra_info,
	};
};

// --- Main Normalization Function ---

/**
 * Normalizes all debugger data from raw extra attributes.
 * This is the single entry point for preparing data for all debugger panels.
 *
 * Search results are extracted from both plugin invocations and minion outputs,
 * with plugin data taking precedence when available.
 *
 * @param extraAttributes - Raw extra attributes from the AI Answer context
 * @param sources - Sources from commonAttributes.sources
 * @returns Normalized data object with all data needed for each debugger panel
 */
export const normalizeDebuggerData = (
	extraAttributes: RawDebuggerData | undefined,
	sources?: NLPSearchResultSource[],
): NormalizedDebuggerData => {
	const {
		originalQuery,
		transformedQuery,
		traceId,
		answerGenerationModel,
		cacheInfo,
		sources: extraSources = [],
		pluginInvocations = [],
		minion_outputs = [],
		rawJson,
	} = extraAttributes ?? {};

	const resolvedSources = sources !== undefined ? sources : extraSources;

	// Extract search data from both plugins and minions, then merge
	const pluginSearchInfo = getSearchResultsFromPlugins(pluginInvocations);
	const minionSearchInfo = getSearchResultsFromMinions(minion_outputs);
	const searchExtraInfo = mergeSearchExtraInfo(pluginSearchInfo, minionSearchInfo);
	const rankedDocumentsWithChunks = getRankedDocumentsWithChunks(searchExtraInfo);

	const orchestrationRounds = getOrchestrationRounds(minion_outputs);

	return {
		requestOverview: {
			originalQuery: originalQuery ?? null,
			transformedQuery: transformedQuery ?? null,
			traceId: traceId ?? null,
			modelName: extractModelName(answerGenerationModel),
			cacheHit: normalizeCacheHit(cacheInfo),
			sources: resolvedSources,
			invokedPluginNames: extractUniquePluginNames(pluginInvocations),
		},
		rankedSearch: {
			searchExtraInfo,
			rankedDocumentsWithChunks,
		},
		rawSearch: {
			searchResults: searchExtraInfo?.searchResults ?? [],
		},
		pluginInvocations: {
			allPluginInvocations: pluginInvocations,
			nonSearchPluginInvocations: filterNonSearchPlugins(pluginInvocations),
			hasPluginInvocations: pluginInvocations ? pluginInvocations.length > 0 : false,
		},
		minions: {
			allMinionOutputs: minion_outputs,
			nonSearchMinionOutputs: filterNonSearchMinions(minion_outputs),
			hasMinionOutputs: minion_outputs ? minion_outputs.length > 0 : false,
			orchestrationRounds,
			roundCount: orchestrationRounds.length,
		},
		rawJson: {
			rawJson,
		},
	};
};
